import { useMemo } from 'react';
import { useCurrentRefinements } from 'react-instantsearch';

import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';
import { isEffectivePriceAttribute } from '@utils/algolia/priceContext';

/**
 * Collapses the attribute aliases used across the filter UI onto a single
 * key so refinement counts land on the same key regardless of which alias a
 * surface asks with (e.g. `categories.lvl2` and `categories.lvl0` are the same
 * category facet; `price` and any `prices.<tier>.effectivePrice` are the same
 * price facet).
 */
export const normalizeFacetKey = (
  attribute: string,
  priceAttribute: string
): string => {
  if (attribute.startsWith('categories')) return 'categories';
  if (attribute === AlgoliaFacets.Price || isEffectivePriceAttribute(attribute))
    return priceAttribute;
  if (
    attribute === AlgoliaFacets.Colour ||
    attribute === AlgoliaFacets.ColourVariants
  )
    return AlgoliaFacets.ColourVariants;
  if (
    attribute === AlgoliaFacets.Rating ||
    attribute === AlgoliaFacets.ReviewScoreFlattened
  )
    return AlgoliaFacets.ReviewScoreFlattened;
  // The size-bucket widget owns every `size.<bucket>` attribute and the
  // shoeWidth dropdown nested inside it, but renders under the synthetic
  // `size` facet entry — so their refinements have to count against `size`.
  if (attribute.startsWith('size.') || attribute === AlgoliaFacets.ShoeWidth)
    return 'size';
  return attribute;
};

/**
 * Active refinement counts per facet, keyed by normalized facet key. Drives
 * the pill badges, the per-accordion counts and the "N filters selected"
 * subtitle. A price range counts as one refinement (its min and max are one
 * filter, not two); the store-availability toggle is not a filter chip and is
 * excluded, matching the current-refinements chips row.
 */
export const useRefinementCounts = (priceAttribute: string) => {
  const { items } = useCurrentRefinements();

  return useMemo(() => {
    const counts = new Map<string, number>();

    for (const item of items) {
      if (item.attribute === AlgoliaFacets.StoreInventory) continue;
      // The current category is navigation, not a user-selected filter — the
      // current-refinements chips exclude it the same way (see Header's
      // excludedAttributes).
      if (item.attribute.startsWith('categories')) continue;

      const key = normalizeFacetKey(item.attribute, priceAttribute);
      const isPrice = key === priceAttribute;
      const increment = isPrice
        ? Math.min(item.refinements.length, 1)
        : item.refinements.length;

      counts.set(key, (counts.get(key) ?? 0) + increment);
    }

    let total = 0;
    counts.forEach((count) => {
      total += count;
    });

    return { counts, total };
  }, [items, priceAttribute]);
};
